import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  AlertCircle,
  CheckCircle2,
  BarChart3,
  ArrowRight
} from 'lucide-react';
import { assets, workOrders, getBuildingById, costHistory } from '@/data/mockData';
import { getMergedWorkOrders } from '@/data/faultStore';
import { predictCosts, getNextPeriodLabels } from '@/lib/forecast';

/* Grafy na stránce jsou vlastní (bez Recharts), aby nedocházelo k chybám ResponsiveContainer/minPointSize. */

const RepairVsReplaceCard = ({ asset, decision }) => {
  const { t } = useLanguage();
  
  const isReplaceRecommended = decision.recommendation === 'replace';
  const savingsPercent = ((decision.repairCostTotal - decision.replacementCost) / decision.repairCostTotal * 100).toFixed(0);

  return (
    <Card className={`shadow-soft border-2 ${
      isReplaceRecommended ? 'border-success' : 'border-warning'
    }`}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{asset.name}</CardTitle>
            <CardDescription>{decision.buildingName}</CardDescription>
          </div>
          <Badge variant={isReplaceRecommended ? 'default' : 'outline'} className="text-xs">
            {isReplaceRecommended ? 'Replace' : 'Continue Repair'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">{t('repairCosts')} (Total)</p>
              <p className="text-2xl font-bold text-destructive">
                {(decision.repairCostTotal / 1000).toFixed(0)}k Kč
              </p>
              <p className="text-xs text-muted-foreground">
                Past 12 months: {decision.repairCount} repairs
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">{t('replacementCost')}</p>
              <p className="text-2xl font-bold text-success">
                {(decision.replacementCost / 1000).toFixed(0)}k Kč
              </p>
              <p className="text-xs text-muted-foreground">
                Estimated lifespan: {decision.newLifespan} years
              </p>
            </div>
          </div>

          {isReplaceRecommended && (
            <div className="bg-success-light p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-success" />
                <p className="font-semibold text-success">{t('recommendation')}</p>
              </div>
              <p className="text-sm text-foreground">
                Replacement recommended - save {Math.abs(savingsPercent)}% over next 2 years
              </p>
            </div>
          )}

          {!isReplaceRecommended && (
            <div className="bg-warning-light p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-warning" />
                <p className="font-semibold text-warning">{t('recommendation')}</p>
              </div>
              <p className="text-sm text-foreground">
                Continue repairs - replacement not cost-effective yet
              </p>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Cost efficiency</span>
              <span>{isReplaceRecommended ? '85%' : '65%'}</span>
            </div>
            <Progress value={isReplaceRecommended ? 85 : 65} className="h-2" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const RepairVsReplacePage = () => {
  const { t } = useLanguage();

  const allWorkOrders = getMergedWorkOrders(workOrders);
  const criticalAssets = assets.filter(a =>
    a.status === 'critical' || a.status === 'maintenance'
  );

  const analysisData = criticalAssets.slice(0, 6).map(asset => {
    const assetWOs = allWorkOrders.filter(wo => wo.assetId === asset.id);
    const repairCostTotal = assetWOs.reduce((sum, wo) => sum + (wo.opex || 0) + (wo.capex || 0), 0);
    const fallbackRepairCost = repairCostTotal === 0 ? asset.totalCost * 0.25 : repairCostTotal;
    const repairCount = assetWOs.length;
    const replacementCost = asset.totalCost * 1.2;
    const effectiveRepairTotal = repairCostTotal > 0 ? repairCostTotal : fallbackRepairCost;

    return {
      asset,
      decision: {
        repairCostTotal: effectiveRepairTotal,
        replacementCost,
        repairCount,
        newLifespan: 10,
        buildingName: getBuildingById(asset.buildingId)?.name ?? '—',
        recommendation: effectiveRepairTotal > replacementCost * 0.7 ? 'replace' : 'repair'
      }
    };
  });

  const replaceRecommended = analysisData.filter(d => d.decision.recommendation === 'replace').length;
  const totalSavings = analysisData
    .filter(d => d.decision.recommendation === 'replace')
    .reduce((sum, d) => sum + (d.decision.repairCostTotal - d.decision.replacementCost) * 2, 0);

  const forecastData = [
    { year: '2024', withReplacement: 450, withoutReplacement: 480 },
    { year: '2025', withReplacement: 420, withoutReplacement: 520 },
    { year: '2026', withReplacement: 380, withoutReplacement: 580 },
    { year: '2027', withReplacement: 350, withoutReplacement: 640 },
    { year: '2028', withReplacement: 320, withoutReplacement: 720 },
  ];

  const forecastMax = Math.max(...forecastData.flatMap(d => [d.withReplacement, d.withoutReplacement]), 1);
  const FORECAST_CHART_HEIGHT = 200;

  const periodsAhead = 3;
  const { predictedOpex, predictedCapex, recommendation } = predictCosts(costHistory, periodsAhead);
  const nextPeriodLabels = getNextPeriodLabels(costHistory, periodsAhead);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2">{t('repairVsReplace')}</h1>
        <p className="text-muted-foreground">
          Data-driven CAPEX decision analysis
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="shadow-soft border-primary border-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Assets Analyzed</p>
                <p className="text-3xl font-bold text-foreground">{analysisData.length}</p>
              </div>
              <div className="p-3 bg-primary-light rounded-lg">
                <BarChart3 className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-soft border-success border-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Replace Recommended</p>
                <p className="text-3xl font-bold text-foreground">{replaceRecommended}</p>
              </div>
              <div className="p-3 bg-success-light rounded-lg">
                <TrendingUp className="w-6 h-6 text-success" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-soft border-accent border-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('projectedSavings')}</p>
                <p className="text-2xl font-bold text-accent">
                  {(totalSavings / 1000).toFixed(0)}k Kč
                </p>
                <p className="text-xs text-muted-foreground mt-1">Over 2 years</p>
              </div>
              <div className="p-3 bg-success-light rounded-lg">
                <DollarSign className="w-6 h-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Predikce nákladů (z costHistory) */}
      <Card className="shadow-soft border-primary border-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Predikce nákladů (další období)
          </CardTitle>
          <CardDescription>
            Odhad OPEX/CAPEX na základě historických trendů
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {nextPeriodLabels.map((label, i) => (
              <div key={label} className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm font-medium text-muted-foreground mb-1">{label}</p>
                <p className="text-lg font-bold text-foreground">
                  OPEX: {(predictedOpex[i] / 1000).toFixed(0)}k Kč
                </p>
                <p className="text-sm text-foreground">
                  CAPEX: {(predictedCapex[i] / 1000).toFixed(0)}k Kč
                </p>
              </div>
            ))}
          </div>
          <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
            <p className="text-sm font-medium text-foreground">{recommendation}</p>
          </div>
        </CardContent>
      </Card>

      {/* Cost Forecast – jednoduchý sloupcový graf bez Recharts */}
      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            {t('financialForecast')}
          </CardTitle>
          <CardDescription>
            Projected annual maintenance costs with and without asset replacement
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-end justify-between gap-2 px-1" style={{ height: FORECAST_CHART_HEIGHT }}>
              {forecastData.map((d) => {
                const withH = Math.max(4, (FORECAST_CHART_HEIGHT * d.withReplacement) / forecastMax);
                const withoutH = Math.max(4, (FORECAST_CHART_HEIGHT * d.withoutReplacement) / forecastMax);
                return (
                  <div key={d.year} className="flex-1 flex flex-col items-center gap-2 min-w-0 max-w-[20%]">
                    <div className="w-full flex gap-1 justify-center items-end" style={{ height: FORECAST_CHART_HEIGHT }}>
                      <div
                        className="flex-1 min-w-[14px] max-w-[28px] rounded-t-sm bg-success shadow-sm"
                        style={{ height: withH }}
                        title={`With replacement: ${d.withReplacement}k`}
                      />
                      <div
                        className="flex-1 min-w-[14px] max-w-[28px] rounded-t-sm bg-destructive/80 shadow-sm"
                        style={{ height: withoutH }}
                        title={`Without replacement: ${d.withoutReplacement}k`}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground font-medium">{d.year}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-6 pt-2 border-t border-border text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded-sm bg-success shrink-0" /> With replacement</span>
              <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded-sm bg-destructive shrink-0" /> Without replacement</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Individual Asset Analysis */}
      <div>
        <h2 className="text-xl font-bold text-foreground mb-4">{t('individualAssetAnalysis')}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {analysisData.map(({ asset, decision }) => (
            <RepairVsReplaceCard key={asset.id} asset={asset} decision={decision} />
          ))}
        </div>
      </div>
    </div>
  );
};
